import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { CustomField, CustomFieldType } from '../types';

interface CustomFieldsManagerProps {
  fields: CustomField[];
  onChange: (fields: CustomField[]) => void;
}

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Texto Curto' },
  { value: 'textarea', label: 'Texto Longo' },
  { value: 'number', label: 'Número' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefone' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Seleção (Dropdown)' },
  { value: 'checkbox', label: 'Checkbox' },
];

const CustomFieldsManager: React.FC<CustomFieldsManagerProps> = ({ fields, onChange }) => {
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const textareaRefs = React.useRef<{ [key: string]: HTMLTextAreaElement | null }>({});

  const generateFieldId = () => {
    return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const addField = () => {
    const newField: CustomField = {
      id: generateFieldId(),
      name: '',
      label: '',
      type: 'text',
      required: false,
      order: fields.length + 1,
    };
    onChange([...fields, newField]);
    setExpandedField(newField.id);
  };

  const updateField = (index: number, updates: Partial<CustomField>) => {
    const updatedFields = [...fields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    
    // Auto-generate name from label if label changes
    if (updates.label && updatedFields[index].name) {
      updatedFields[index].name = updates.label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '_');
    }
    
    onChange(updatedFields);
  };

  const removeField = (index: number) => {
    const updatedFields = fields.filter((_, i) => i !== index);
    // Reorder remaining fields
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    onChange(updatedFields);
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === fields.length - 1)
    ) {
      return;
    }

    const updatedFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [updatedFields[index], updatedFields[targetIndex]] = 
    [updatedFields[targetIndex], updatedFields[index]];
    
    // Update order
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    
    onChange(updatedFields);
  };

  const toggleExpanded = (fieldId: string) => {
    setExpandedField(expandedField === fieldId ? null : fieldId);
  };

  const updateOptions = (index: number, optionsText: string) => {
    // Não remove linhas vazias durante a digitação
    // Apenas split por \n e mantém tudo
    const options = optionsText.split('\n');
    updateField(index, { options });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Perguntas Extras
          </label>
          <p className="text-xs text-gray-500 mt-0.5">
            Outras informações que você quer coletar (Ex: Data de nascimento, Cidade)
          </p>
        </div>
        <button
          type="button"
          onClick={addField}
          className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-1" />
          Adicionar Pergunta
        </button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-sm text-gray-500">
            Nenhuma pergunta extra adicionada.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Clique em "Adicionar Pergunta" para começar.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="border border-gray-300 rounded-lg bg-white shadow-sm"
            >
              {/* Header */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-t-lg">
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => moveField(index, 'up')}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Mover para cima"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveField(index, 'down')}
                    disabled={index === fields.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Mover para baixo"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {field.label || `Campo ${index + 1}`}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({FIELD_TYPES.find(t => t.value === field.type)?.label})
                    </span>
                    {field.required && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded w-fit">
                        Obrigatório
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => toggleExpanded(field.id)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  {expandedField === field.id ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => removeField(index)}
                  className="text-red-600 hover:text-red-900 flex-shrink-0"
                  title="Remover campo"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Expanded Content */}
              {expandedField === field.id && (
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Rótulo (exibido ao usuário) *
                      </label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(index, { label: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="Ex: Cidade"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Nome técnico *
                      </label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => updateField(index, { name: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="Ex: city"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tipo de campo *
                      </label>
                      <select
                        value={field.type}
                        onChange={(e) => updateField(index, { type: e.target.value as CustomFieldType })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        {FIELD_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(index, { required: e.target.checked })}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-xs font-medium text-gray-700">
                          Campo obrigatório
                        </span>
                      </label>
                    </div>
                  </div>

                  {field.type === 'select' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Opções (uma por linha) *
                      </label>
                      <textarea
                        ref={(el) => { textareaRefs.current[field.id] = el; }}
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => updateOptions(index, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 font-mono"
                        rows={6}
                        placeholder="Digite cada opção e pressione Enter:&#10;Opção 1&#10;Opção 2&#10;Opção 3"
                        style={{ resize: 'vertical', minHeight: '100px' }}
                      />
                      <div className="mt-2 flex items-start justify-between gap-2">
                        <p className="text-xs text-gray-500">
                          💡 Pressione <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Enter</kbd> para adicionar nova opção
                        </p>
                        {field.options && field.options.filter(opt => opt.trim().length > 0).length > 0 && (
                          <p className="text-xs text-green-600 font-medium whitespace-nowrap">
                            ✓ {field.options.filter(opt => opt.trim().length > 0).length} {field.options.filter(opt => opt.trim().length > 0).length === 1 ? 'opção' : 'opções'}
                          </p>
                        )}
                      </div>
                      {field.options && field.options.filter(opt => opt.trim().length > 0).length > 0 && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-xs font-medium text-blue-900 mb-1">Preview:</p>
                          <div className="flex flex-wrap gap-1">
                            {field.options.filter(opt => opt.trim().length > 0).map((option, optIdx) => (
                              <span
                                key={optIdx}
                                className="inline-flex items-center px-2 py-1 text-xs bg-white border border-blue-300 rounded text-blue-900"
                              >
                                {option.trim()}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {fields.length > 0 && (
        <p className="text-xs text-gray-500">
          💡 Os campos serão exibidos nesta ordem no formulário de participação
        </p>
      )}
    </div>
  );
};

export default CustomFieldsManager;
